import React, { useState } from 'react';
import { PriceArea } from '../types';
import { Mail, Loader, CheckCircle, XCircle, MapPin } from 'lucide-react';
import { getPriceAreaInfo } from '../utils/api';

interface MorningReportProps {
  area: PriceArea;
}

const MorningReport: React.FC<MorningReportProps> = ({ area }) => {
  const areaInfo = getPriceAreaInfo().find(a => a.id === area);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/morning-report`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          email,
          priceArea: area,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Kunne ikke registrere abonnement');
      }

      setSuccess(true);
      setEmail('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'En feil oppstod');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white dark:bg-dark-card rounded-lg shadow-sm overflow-hidden dark:border dark:border-dark-border">
      <div className="bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 p-4 sm:p-6">
        <div className="flex items-center">
          <div className="bg-purple-100 dark:bg-purple-800 p-2 rounded-md mr-3">
            <Mail className="h-5 w-5 text-purple-600 dark:text-purple-400" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg sm:text-xl font-medium text-gray-900 dark:text-white whitespace-nowrap">Morgenrapport</h3>
            <div className="flex items-center mt-1">
              <MapPin className="h-4 w-4 text-gray-500 dark:text-gray-400 mr-1" />
              <p className="text-sm text-gray-600 dark:text-gray-300">{areaInfo?.description}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="p-4 sm:p-6">
        {success ? (
          <div className="flex items-center text-green-700 dark:text-green-300 mb-4">
            <CheckCircle className="h-5 w-5 mr-2" />
            <p>Takk! Du vil nå motta morgenrapporten for {areaInfo?.description} hver dag kl. 07:00.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Motta daglig rapport for {areaInfo?.description}
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="din@epost.no"
                className="w-full px-4 py-3 text-base border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-800 dark:text-white"
                required
              />
            </div>

            {error && (
              <div className="flex items-center text-red-600 dark:text-red-400 text-sm">
                <XCircle className="h-4 w-4 mr-1" />
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className={`w-full flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 ${
                loading ? 'opacity-75 cursor-not-allowed' : ''
              }`}
            >
              {loading ? (
                <>
                  <Loader className="animate-spin h-4 w-4 mr-2" />
                  Registrerer...
                </>
              ) : (
                'Meld meg på morgenrapporten'
              )}
            </button>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
              Du vil motta en daglig e-post med strømpriser og beste ladetider for {areaInfo?.description}.
            </p>
          </form>
        )}

        <div className="mt-4 text-xs text-gray-500 dark:text-gray-400">
          <p>Ved å melde deg på godtar du at vi sender deg en daglig e-post med strømpriser for ditt område. Du kan når som helst melde deg av.</p>
        </div>
      </div>
    </div>
  );
};

export default MorningReport;